import {Loader, Stack} from '@mantine/core'
import {useQuery} from '@tanstack/react-query'
import {createFileRoute} from '@tanstack/react-router'

import {getProspectsByEngagement} from 'src/api/client/api'
import CapacityEngagementMatrix, {
    CapacityEngagementMatrixExportContent,
} from 'src/components/analytics/capacity-engagement-matrix'
import {ManagerFilter} from 'src/components/analytics/manager-filter'
import {useProspectNavigation} from 'src/components/analytics/use-prospect-navigation'
import {ExportImageButton} from 'src/components/export-image-button'
import {PageHeader} from 'src/components/page-header'
import {useScenarioPlanning} from 'src/components/scenario/scenario-context'

type SearchParams = {manager?: string}

export const Route = createFileRoute('/_authed/organizations/$orgId/prospects-by-engagement')({
    component: ProspectsByEngagementPage,
    validateSearch: (search: Record<string, unknown>): SearchParams => ({
        manager: (search['manager'] as string) || undefined,
    }),
})

function ProspectsByEngagementPage() {
    const {orgId} = Route.useParams()
    const {manager} = Route.useSearch()
    const navigateToProspects = useProspectNavigation(orgId)
    const {appliedScenario} = useScenarioPlanning()

    const {data, isLoading, error} = useQuery({
        queryKey: ['page', orgId, 'prospects-by-engagement', manager, appliedScenario],
        queryFn: () => getProspectsByEngagement(orgId, manager, appliedScenario),
    })

    if (isLoading) {
        return <Loader size="lg" style={{display: 'block', margin: '60px auto'}} />
    }

    if (error) {
        return <div>Error loading page: {(error as Error).message}</div>
    }

    if (!data) return null

    const headerTitle = 'Capacity and Engagement'
    const headerDescription = 'Breakdown of prospects by engagement level and capacity.'
    const matrixTitle = 'Capacity and Engagement'

    return (
        <Stack>
            <PageHeader
                title={headerTitle}
                description={headerDescription}
                actions={
                    <ExportImageButton
                        title={headerTitle}
                        renderContent={({kind}) => (
                            <CapacityEngagementMatrixExportContent data={data} kind={kind} />
                        )}
                        exportWidthPx={{chart: 880, table: 900}}
                        filenameBase="prospects-by-engagement"
                    />
                }
            />
            <ManagerFilter orgId={orgId} />
            <CapacityEngagementMatrix
                data={data}
                title={matrixTitle}
                onProspectFilter={(filters) => navigateToProspects({...filters, manager})}
            />
        </Stack>
    )
}
